/**
 * `RecoveryFlow` — orchestrator for the recovery-flow state machine.
 *
 * Lifts the subscriber + getState pattern from `auth/auth-flow.ts`
 * and `export/export-flow.ts`. State transitions via discriminated-
 * union reducer (`applyEvent` from `./state.ts`); API calls via
 * `IProjectionServiceClient` recovery endpoints per design memo
 * PR #156 §4 (5 HTTP endpoints DEV3 ratified).
 *
 * **Phase B (post-PR-290 partial integration).** GC dispositioned
 * Issue #48 (Q1/Q3/Q4 concurred) and DEV3 shipped Phase B-cryptographic-
 * mechanism canonical-constants extraction at
 * [PR #290](https://github.com/SmartHealthNetwork/SHN2026/pull/290).
 * Client now consumes the canonical surface via the mirror at
 * `src/recovery/canonical-constants.ts` (drift-detected via
 * `test/recovery/canonical-constants-binding.test.ts`). Storage
 * swap-in is no-surface-change at the wire layer per
 * [PR #275 §7](https://github.com/SmartHealthNetwork/SHN2026/pull/275)
 * — Phase B-storage cloud cohort ([PR #281](https://github.com/SmartHealthNetwork/SHN2026/pull/281))
 * preserves the `IProjectionServiceClient` contract.
 *
 * **Phase B remaining (cryptographic submitPassphrase substantive).**
 * Current `submitPassphrase` calls `finalizeRecovery` (which now
 * returns canonical-shaped `{wrappedPmkCiphertext, recoveryPassphraseSalt,
 * pbkdf2Iterations, coolOffUntil}`) and dispatches `reconstruction-complete`
 * without performing the actual client-side KDF derivation + PMK
 * unwrap. The substantive cryptographic integration:
 *   1. Client derives recovery-passphrase key via
 *      `adapter.derivePMK({ authFactor: passphrase, salt:
 *      response.recoveryPassphraseSalt, iterations:
 *      response.pbkdf2Iterations })` — canonical-constants byte-for-
 *      byte parity with server-side KDF
 *   2. Client unwraps `response.wrappedPmkCiphertext` via
 *      `adapter.unwrapKey({ outerKey: derivedKey, wrappedBytes,
 *      aad, usages: ['wrapKey', 'unwrapKey'] })` — recovery-passphrase-
 *      derived key unwraps the wrapped-PMK ciphertext
 *   3. Resulting PMK loads into `ActiveSession` via
 *      `session.loadUnwrappedKek` (post-cool-off; KEK rewrap under
 *      new auth factors composes separately)
 * AAD specification + KEK rewrap protocol need joint sign-off with
 * DEV3 backend at the wrappedPmkCiphertext-AAD-shape moment; deferred
 * to next Phase B-cryptographic-substantive integration cycle.
 *
 * **Doctrinal posture preserved at the orchestrator layer:**
 *   - Recovery passphrase NEVER leaves the client (NO SHT BACKDOOR;
 *     Doctrine v1.3 §6/§8 A.4/B.1 first non-negotiability marker).
 *     Phase B integration: passphrase + salt → canonical KDF derived
 *     key → unwraps server-returned wrapped-PMK ciphertext client-side.
 *   - Recovery is NOT erasure (ADR-024 §2.7.5 + Doctrine v1.3 §8).
 *     Failure-unrecoverable terminal state offers reactivate-from-
 *     holders pathway; never routes to erasure UI.
 *   - Recovery UX honesty (Doctrine v1.3 §8 B.3 ELEVATE second
 *     non-negotiability marker per `feedback_phg_recovery_ux_honesty`):
 *     "content unrecoverable" framing must survive UX simplification.
 *   - Cooling-off period substrate-enforced; client polls
 *     `/cool-off-status` for the time-source-authority signal.
 *   - Pattern B no-parallel-rendering-surface discipline: client
 *     uses canonical constants for read-only display surface
 *     confirmation; enforcement remains backend-side per
 *     `feedback_pattern_b_no_parallel_rendering_surface`.
 */
import { applyEvent, isTerminal, } from './state.js';
import { EnvelopeCryptoAdapterError, } from '../crypto/envelope-crypto-adapter.js';
import { aeadDecrypt, deriveCombinedUnwrapKey, deriveLayerEvidenceKey, encodeLayerEvidenceKeyInfo, EnvelopeCryptoError, zeroizeBytes, } from '@shn/envelope-crypto';
import { encodeRecoveryAad, ENVELOPE_VERSION_V1, KDF_ALGORITHM_V1, } from './aad-canonical-encoder.js';
import { encodeShareAad } from './share-aad-encoder.js';
import { combineShares, decodeShamirShare, } from './threshold-combine.js';
/**
 * Canonical KekId under which the recovery-derived PMK loads into
 * `IActiveSession`. Per `recovery_flow_phase_b_step_3b_design.md` v0.3
 * §3.1 Step 5 + §3.2 amendment per [DEV1 axis-2 review pushback at
 * issuecomment-4405428045](https://github.com/SmartHealthNetwork/SHN2026/pull/339#issuecomment-4405428045):
 * existing `loadUnwrappedKek` surface at `active-session.ts:120-171`
 * is canonical-vocabulary-aligned; PMK = KEK shape match (non-extractable
 * AES-256-GCM `CryptoKey` with `wrapKey`/`unwrapKey` usages).
 */
const RECOVERY_PMK_KEK_ID = 'pmk';
/**
 * Canonical N=5/k=3 threshold per
 * [recovery_flow_backend_design.md §3.2](recovery_flow_backend_design.md)
 * + [GC Issue #48 disposition](https://github.com/SmartHealthNetwork/SHN2026/issues/48#issuecomment-4393749031)
 * Q3 substantive concurrence.
 */
const RECOVERY_SHAMIR_THRESHOLD = 3;
export class RecoveryFlow {
    options;
    state = { kind: 'intro' };
    subscribers = new Set();
    /**
     * Collected Shamir shares for the in-flight recovery transcript.
     * Active-session-only per `feedback_phg_client_active_session_crypto.md`;
     * cleared on `restart()`. Populated by `pushCollectedShare` (called
     * from §3.4 submitLayer cycle extension once shipped, OR by tests
     * directly).
     */
    collectedShamirShares = [];
    constructor(options) {
        this.options = options;
    }
    pushCollectedShare(share) {
        this.collectedShamirShares.push(share);
    }
    getState() {
        return this.state;
    }
    subscribe(callback) {
        this.subscribers.add(callback);
        callback(this.state);
        return () => this.subscribers.delete(callback);
    }
    dispatch(event) {
        try {
            this.state = applyEvent(this.state, event);
        }
        catch (e) {
            this.state = {
                kind: 'error',
                code: 'transition-error',
                message: e instanceof Error ? e.message : String(e),
            };
        }
        for (const sub of this.subscribers) {
            sub(this.state);
        }
    }
    beginTierElection() {
        this.dispatch({ kind: 'begin-tier-election' });
    }
    async submitTier(tier) {
        try {
            const response = await this.options.client.initiateRecovery({
                subjectPciHash: this.options.subject.subjectPciHash,
                ...(this.options.subject.tenantId !== undefined
                    ? { tenantId: this.options.subject.tenantId }
                    : {}),
                tier,
            });
            if (response.outcome !== 'ok') {
                this.dispatch({
                    kind: 'recovery-failed',
                    outcome: 'recoverable',
                    escalationPathway: 'shdt-staff-channel',
                    reason: response.outcome,
                });
                return;
            }
            this.dispatch({
                kind: 'submit-tier',
                tier,
                sessionId: response.sessionId,
                requiredLayers: response.requiredLayers,
                coolOffSeconds: response.coolOffSeconds,
            });
        }
        catch (e) {
            this.dispatch({
                kind: 'error',
                code: 'initiate-failed',
                message: e instanceof Error ? e.message : String(e),
            });
        }
    }
    async submitLayer(opts) {
        if (this.state.kind !== 'verification-layers') {
            this.dispatch({
                kind: 'error',
                code: 'invalid-state',
                message: `submitLayer called in state ${this.state.kind}`,
            });
            return;
        }
        const sessionId = this.state.sessionId;
        const subjectPciHash = this.options.subject.subjectPciHash;
        this.dispatch({
            kind: 'layer-status-changed',
            layer: opts.layer,
            status: 'in-progress',
        });
        try {
            const response = await this.options.client.submitRecoveryLayer({
                sessionId,
                layerKind: opts.layer,
                evidence: opts.evidence,
            });
            let nextStatus;
            switch (response.outcome) {
                case 'completed':
                    nextStatus = 'completed';
                    break;
                case 'in-progress':
                case 'awaiting-cross-session':
                    nextStatus = 'awaiting-cross-session';
                    break;
                case 'accommodation-requested':
                    nextStatus = 'accommodation-requested';
                    break;
                case 'rejected':
                case 'pattern-detected':
                    nextStatus = 'failed';
                    break;
                default:
                    nextStatus = 'failed';
            }
            // **Substantive Step 3b §3.4 wire-up** per
            // `recovery_flow_phase_b_step_3b_design.md` v0.3 §3.4 worked
            // example. When the layer carries recovery-share material
            // (substrate-side `LayerEvidence.possession.recoveryShareCiphertext`
            // per memo §2.2 OQ3 disposition + DEV5 wire-contract ping at
            // [PR #364 trail issuecomment-4407137310](https://github.com/SmartHealthNetwork/SHN2026/pull/364)
            // three-way DEV1+DEV5+DEV3 disposition (a) convergence) the
            // client re-derives the layer-evidence-key via
            // `deriveLayerEvidenceKey` (PR #386), AEAD-decrypts the per-
            // share ciphertext under the canonical share-AAD, and pushes
            // the decoded `ShamirShare` into the in-flight transcript for
            // §3.1 `submitPassphrase` Step 2 `combineShares` consumption.
            //
            // Failure mode discipline per `feedback_phg_recovery_ux_honesty`
            // + Doctrine v1.3 §8 B.3 ELEVATE: any per-share decrypt /
            // decode / canonical-encoder failure surfaces as the generic
            // `'unrecoverable'` outcome (NOT factor-discriminating); top-
            // level transitions through `recovery-failed` per existing
            // verification-layers state semantics.
            if (response.outcome === 'completed' &&
                opts.evidenceMaterial !== undefined &&
                response.recoveryShareCiphertext !== undefined &&
                response.shareIndex !== undefined &&
                response.recoveryEnvelopeId !== undefined) {
                // §3.4 per-share decrypt branch entered ONLY when caller signals
                // intent (provides `evidenceMaterial`). When caller omits the IKM,
                // the layer completes per legacy semantics + the in-flight transcript
                // stays empty; the `combineShares` failure at §3.1 `submitPassphrase`
                // surfaces the resulting recovery as generic `'unrecoverable'` per
                // existing §3.1 cryptographic-failure discipline. End-user surface
                // is doctrinally equivalent in both cases per `feedback_phg_recovery_
                // ux_honesty` non-discriminating discipline. Tests that exercise
                // §3.1 in isolation (using `pushCollectedShare` test seam) preserve
                // their no-evidenceMaterial submitLayer call shape.
                let sharePlaintext;
                try {
                    const info = encodeLayerEvidenceKeyInfo({
                        layerKind: opts.layer,
                        subjectPciHash,
                        recoveryEnvelopeId: response.recoveryEnvelopeId,
                        shareIndex: response.shareIndex,
                    });
                    const layerEvidenceKey = await deriveLayerEvidenceKey({
                        evidenceMaterial: opts.evidenceMaterial,
                        info,
                    });
                    // Caller-owned IKM zeroized post-derivation per active-session-
                    // only crypto discipline (`feedback_phg_client_active_session_crypto`);
                    // derived `CryptoKey` is non-extractable and outlives this scope.
                    zeroizeBytes(opts.evidenceMaterial);
                    const shareAad = encodeShareAad({
                        subjectPciHash,
                        shareIndex: response.shareIndex,
                        recoveryEnvelopeId: response.recoveryEnvelopeId,
                    });
                    sharePlaintext = await aeadDecrypt(response.recoveryShareCiphertext, layerEvidenceKey, shareAad);
                    const share = decodeShamirShare(sharePlaintext);
                    // `decodeShamirShare` copies `y` into a fresh buffer; the
                    // plaintext can be zeroized without affecting the held share.
                    zeroizeBytes(sharePlaintext);
                    sharePlaintext = undefined;
                    this.collectedShamirShares.push(share);
                }
                catch {
                    if (sharePlaintext !== undefined) {
                        zeroizeBytes(sharePlaintext);
                    }
                    // Generic `'unrecoverable'` surface: catches `EnvelopeCryptoError`
                    // (HKDF / AEAD / decode), `EnvelopeCryptoAdapterError`, and
                    // plain `Error` from canonical encoders (`encodeShareAad`).
                    this.dispatch({
                        kind: 'layer-status-changed',
                        layer: opts.layer,
                        status: 'failed',
                    });
                    this.dispatch({ kind: 'recovery-failed', outcome: 'unrecoverable' });
                    return;
                }
            }
            this.dispatch({
                kind: 'layer-status-changed',
                layer: opts.layer,
                status: nextStatus,
                ...(response.reason !== undefined ? { failureReason: response.reason } : {}),
            });
            // Check if all layers complete; advance state
            if (this.state.kind === 'verification-layers') {
                const allDone = this.state.layerStates.every((ls) => ls.status === 'completed');
                if (allDone) {
                    this.dispatch({ kind: 'all-layers-complete' });
                }
            }
        }
        catch (e) {
            this.dispatch({
                kind: 'error',
                code: 'layer-submit-failed',
                message: e instanceof Error ? e.message : String(e),
            });
        }
    }
    async submitPassphrase(passphrase) {
        if (this.state.kind !== 'recovery-passphrase') {
            this.dispatch({
                kind: 'error',
                code: 'invalid-state',
                message: `submitPassphrase called in state ${this.state.kind}`,
            });
            return;
        }
        if (passphrase.length === 0) {
            this.dispatch({
                kind: 'recovery-failed',
                outcome: 'recoverable',
                escalationPathway: 'shdt-staff-channel',
                reason: 'passphrase-empty',
            });
            return;
        }
        const sessionId = this.state.sessionId;
        this.dispatch({ kind: 'submit-passphrase' });
        // **Substantive Step 3b §3.1 wire-up under Reading C** per
        // `recovery_flow_phase_b_step_3b_design.md` v0.4. Replaces Phase A
        // stub with the canonical orchestration:
        //   1. backend `finalizeRecovery` fetches canonical envelope
        //      (wrappedPmkCiphertext + recoveryPassphraseSalt +
        //       pbkdf2Iterations + recoveryEnvelopeId + coolOffUntil)
        //   2. `combineShares` reconstructs threshold material from the
        //      collected Shamir shares (populated by §3.4 submitLayer
        //      extension when shipped, OR by tests directly)
        //   3. `encodeRecoveryAad` produces the canonical AAD bytes
        //      (RFC 8785 JCS) bound to envelope + tenant + subject + KDF
        //      params + recovery envelope identifier
        //   4. `deriveCombinedUnwrapKey` HKDF-combines threshold material
        //      + passphrase-derived key with the AAD as transcript
        //   5. `adapter.unwrapKey` produces the non-extractable PMK
        //   6. `session.loadUnwrappedKek({kekId: 'pmk', key: pmk})` loads
        //      the PMK into the active session per existing surface
        //   7. transient material zeroized; reconstruction-complete dispatched
        //
        // Generic failure surface per `feedback_phg_recovery_ux_honesty.md`
        // + Doctrine v1.3 §8 B.3 ELEVATE: any cryptographic-failure path
        // (combine fail / passphrase mismatch / AAD mismatch / unwrap fail)
        // surfaces as `'unrecoverable'` outcome with internal logging
        // available for substrate-engineering observability per memo §2.4.
        let thresholdMaterial;
        try {
            // Step 1: backend finalize fetches canonical envelope
            const response = await this.options.client.finalizeRecovery({ sessionId });
            if (response.outcome !== 'ok') {
                if (response.outcome === 'unrecoverable') {
                    this.dispatch({ kind: 'recovery-failed', outcome: 'unrecoverable' });
                }
                else if (response.outcome === 'escalation-required') {
                    this.dispatch({
                        kind: 'recovery-failed',
                        outcome: 'recoverable',
                        escalationPathway: 'shdt-staff-channel',
                        reason: 'escalation-required',
                    });
                }
                else {
                    this.dispatch({
                        kind: 'recovery-failed',
                        outcome: 'recoverable',
                        escalationPathway: 'shdt-staff-channel',
                        reason: response.outcome,
                    });
                }
                return;
            }
            // Pre-condition guards: outcome=ok envelope MUST carry the canonical
            // payload fields. Missing fields indicate substrate-side wire
            // contract drift (substrate-state-record overclaim risk per
            // `feedback_empirical_verification_before_canonical_state_claim.md`);
            // surface as unrecoverable per Doctrine v1.3 §8 B.3 ELEVATE.
            if (response.wrappedPmkCiphertext === undefined ||
                response.recoveryPassphraseSalt === undefined ||
                response.pbkdf2Iterations === undefined ||
                response.recoveryEnvelopeId === undefined ||
                response.coolOffUntil === undefined) {
                this.dispatch({ kind: 'recovery-failed', outcome: 'unrecoverable' });
                return;
            }
            // Step 2: client-side Shamir-or-equivalent k-of-n combine (memo §2.1).
            // Throws `EnvelopeCryptoError 'invalid-length'` if insufficient shares
            // collected; caught at the generic boundary below.
            thresholdMaterial = combineShares({
                shares: this.collectedShamirShares,
                threshold: RECOVERY_SHAMIR_THRESHOLD,
            });
            // Step 3: encode canonical AAD (RFC 8785 JCS) per memo §3.1 row 6.
            // tenantId is derived from the patient subject (RecoveryFlowSubject);
            // envelopeVersion + kdfAlgorithm pinned to v1 canonical values.
            //
            // Doctrinal-anchor preservation per `feedback_phg_recovery_ux_honesty.md`
            // Doctrine v1.3 §8 B.3 ELEVATE: when `subject.tenantId` is undefined
            // (production session bootstrap misconfig), throw a typed
            // `EnvelopeCryptoError` so the catch boundary below dispatches
            // `'unrecoverable'` per non-discriminating discipline. Prior
            // defensive `?? ''` coercion routed undefined → empty-tenantId →
            // plain `Error('tenantId must be non-empty')` from `encodeRecoveryAad`
            // which fell into the catch block's `else` branch and dispatched
            // `kind: 'error'` (NOT the doctrinally-correct `'failure-unrecoverable'`).
            if (this.options.subject.tenantId === undefined) {
                throw new EnvelopeCryptoError('submitPassphrase: subject.tenantId is required at recovery-time; ' +
                    'production session bootstrap MUST populate it per ' +
                    '`recovery_flow_phase_b_step_3b_design.md` v0.4 §3.1 Step 3 ' +
                    'canonical AAD field set', 'invalid-key');
            }
            const aadBytes = encodeRecoveryAad({
                envelopeVersion: ENVELOPE_VERSION_V1,
                tenantId: this.options.subject.tenantId,
                subjectPciHash: this.options.subject.subjectPciHash,
                pbkdf2Iterations: response.pbkdf2Iterations,
                kdfAlgorithm: KDF_ALGORITHM_V1,
                recoveryEnvelopeId: response.recoveryEnvelopeId,
            });
            // Step 4: HKDF-combine threshold material + passphrase derivation
            // with AAD as transcript. Reading C: both factors required.
            const combinedKey = await deriveCombinedUnwrapKey({
                thresholdMaterial,
                passphrase,
                salt: response.recoveryPassphraseSalt,
                iterations: response.pbkdf2Iterations,
                transcript: aadBytes,
            });
            // Step 5: unwrap wrapped PMK under combined key with AAD-bound
            // integrity. Returns non-extractable CryptoKey per ADR-032 §1.
            // Mismatch in either factor (threshold material or passphrase)
            // surfaces as `EnvelopeCryptoError 'authentication-failed'`.
            const pmk = await this.options.adapter.unwrapKey({
                outerKey: combinedKey,
                wrappedBytes: response.wrappedPmkCiphertext,
                aad: aadBytes,
                usages: ['wrapKey', 'unwrapKey'],
            });
            // Step 6: load PMK into ActiveSession via existing canonical surface
            // (`loadUnwrappedKek({kekId, key})` at active-session.ts:120-171);
            // PMK = KEK shape match per memo §3.2 lifecycle table.
            this.options.session.loadUnwrappedKek({ kekId: RECOVERY_PMK_KEK_ID, key: pmk });
            // Step 7: zeroize transient threshold material + collected shares
            // (defense-in-depth; combined CryptoKey is non-extractable so no
            // raw key bytes leak via this path). thresholdMaterial assigned at
            // Step 2 above; non-null asserted across the await boundary.
            zeroizeBytes(thresholdMaterial);
            thresholdMaterial = undefined;
            for (const share of this.collectedShamirShares) {
                zeroizeBytes(share.y);
            }
            this.collectedShamirShares = [];
            // Step 8: dispatch reconstruction-complete with cool-off-until
            // from canonical response.
            this.dispatch({
                kind: 'reconstruction-complete',
                coolOffUntil: response.coolOffUntil,
            });
        }
        catch (e) {
            // Defense-in-depth: zeroize any transient threshold material on
            // failure path before surfacing generic recovery-failed outcome.
            if (thresholdMaterial !== undefined) {
                zeroizeBytes(thresholdMaterial);
            }
            // Per `feedback_phg_recovery_ux_honesty.md` Doctrine v1.3 §8 B.3
            // ELEVATE: any cryptographic-failure path surfaces as the generic
            // `'unrecoverable'` outcome. Internal observability for substrate-
            // engineering deferred to Sprint 8 LOCKED substrate observability
            // + audit-introspection developer tooling per Lock Memo v2 +
            // memo §2.4.
            // Per `feedback_phg_recovery_ux_honesty.md` Doctrine v1.3 §8 B.3
            // ELEVATE: any cryptographic-failure path (combine fail / passphrase
            // mismatch / AAD mismatch / unwrap fail / load fail) surfaces as the
            // generic `'unrecoverable'` outcome. `EnvelopeCryptoAdapterError`
            // wraps Web Crypto failures from `adapter.unwrapKey` per the adapter
            // contract at `envelope-crypto-adapter.ts:329-345`. Network /
            // transport errors from `finalizeRecovery` already returned a
            // non-`'ok'` outcome above; reaching the catch block here means a
            // crypto-step failure post-finalize-ok.
            if (e instanceof EnvelopeCryptoError || e instanceof EnvelopeCryptoAdapterError) {
                this.dispatch({ kind: 'recovery-failed', outcome: 'unrecoverable' });
            }
            else {
                this.dispatch({
                    kind: 'error',
                    code: 'finalize-failed',
                    message: e instanceof Error ? e.message : String(e),
                });
            }
        }
    }
    cancel(reason) {
        if (isTerminal(this.state))
            return;
        // Defense-in-depth: zeroize any in-flight collected Shamir shares
        // on cancel; active-session-only crypto discipline per
        // `feedback_phg_client_active_session_crypto.md` + ADR-032 §1.
        for (const share of this.collectedShamirShares) {
            zeroizeBytes(share.y);
        }
        this.collectedShamirShares = [];
        this.dispatch({ kind: 'cancel', reason });
    }
    restart() {
        // Active-session-only crypto: zeroize collected Shamir shares on
        // restart per `feedback_phg_client_active_session_crypto.md` +
        // ADR-032 §1; new transcript begins on next tier election.
        for (const share of this.collectedShamirShares) {
            zeroizeBytes(share.y);
        }
        this.collectedShamirShares = [];
        this.state = { kind: 'intro' };
        for (const sub of this.subscribers) {
            sub(this.state);
        }
    }
}
//# sourceMappingURL=recovery-flow.js.map